import { dedupe, ensureCCW, type Pt } from "../math/polygon";
import type { ParametricGeometry } from "../types/input";

const r6 = (v: number): number => Math.round(v * 1e6) / 1e6;

/**
 * Parametrik tanımı CCW çokgene çevirir; burun (0,0) ilk eleman, taban y=0.
 * Eğik tabanda (baseSlope = tanα > 0) burun (0,0) kalır, taban topuğa doğru iner: topuk (B, −B·tanα). Temel bloğu yoksa gövdenin
 * arka alt köşesi, varsa temel bloğunun topuk alt köşesi aşağı iner; gövde tabanı / temel üstü yatay kalır.
 */
export function parametricToPolygon(g: ParametricGeometry): Pt[] {
  const hf = g.foundation?.height ?? 0;
  const toeExt = g.foundation?.toeExtension ?? 0;
  const heelExt = g.foundation?.heelExtension ?? 0;
  const s = g.baseSlope ?? 0;
  const mf = g.frontBatter;
  const Hb = g.steps.reduce((s, st) => s + st.height, 0);
  const xFront = (yBody: number): number => mf * yBody;           // gövde tabanına göre
  const drop = (x: number): number => (s > 0 ? -s * (x + toeExt) : 0);   // taban kotu (burundan x+toeExt uzakta)

  // Saat yönünde dolaşım: gövde ön alt → ön yüz yukarı → taç → arka yüz aşağı → temel
  const pts: Pt[] = [];
  pts.push({ x: 0, y: hf });
  pts.push({ x: xFront(Hb), y: hf + Hb });
  let yTop = Hb;
  for (const st of g.steps) {
    const yBot = yTop - st.height;
    pts.push({ x: xFront(yTop) + st.topThickness, y: hf + yTop });
    const xb = xFront(yBot) + st.bottomThickness;
    pts.push({ x: xb, y: hf + yBot + (!g.foundation && yBot < 1e-9 ? drop(xb) : 0) });
    yTop = yBot;
  }
  if (g.foundation) {
    const last = g.steps[g.steps.length - 1]!;
    const bodyBack = last.bottomThickness;
    pts.push({ x: bodyBack + heelExt, y: hf });
    pts.push({ x: bodyBack + heelExt, y: drop(bodyBack + heelExt) });
    pts.push({ x: -toeExt, y: 0 });
    pts.push({ x: -toeExt, y: hf });
  }
  const shifted = pts.map((p) => ({ x: r6(p.x + toeExt), y: r6(p.y) }));
  const ccw = ensureCCW(dedupe(shifted));
  // Burun (0,0) ilk eleman olsun
  const i0 = ccw.findIndex((p) => Math.abs(p.x) < 1e-9 && Math.abs(p.y) < 1e-9);
  return i0 > 0 ? [...ccw.slice(i0), ...ccw.slice(0, i0)] : ccw;
}

/** İç kesit kontrol kotları: kademe alt sınırları ve gövde tabanı (taban hariç), artan. */
export function jointLevelsOf(g: ParametricGeometry): number[] {
  const hf = g.foundation?.height ?? 0;
  const Hb = g.steps.reduce((s, st) => s + st.height, 0);
  const levels: number[] = [];
  let y = Hb;
  for (let i = 0; i < g.steps.length - 1; i++) { y -= g.steps[i]!.height; levels.push(r6(hf + y)); }
  if (hf > 0) levels.push(r6(hf));
  // Eğik taban, temel bloğu yok: burun kotundan (y = 0) geçen yatay derz gövdenin tabanıdır — gövde bu kesitte eğik taban kamasının
  // üstünde kayabilir; derz kontrolü (N·μ + c·b ≥ GK·V) burada yapılır (temel bloğunda gövde tabanı hf zaten derz)
  else if ((g.baseSlope ?? 0) > 0) levels.push(0);
  return [...new Set(levels)].sort((a, b) => a - b);
}
